import type { ReactNode } from 'react';
import {
  Archive,
  ArchiveRestore,
  ClipboardList,
  Copy,
  Eye,
  MoreHorizontal,
  Pencil,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { ListSurface } from '@/components/layouts';
import { StatusChip } from '@/components/molecules/StatusChip';
import {
  DataGrid,
  type DataGridColumn,
  type DataGridSortDir,
} from '@/components/organisms/data-grid';
import { ChecklistVisibilityBadge } from '@/components/organisms/checklists/ChecklistTemplateReadOnlyBlocks';
import type { ChecklistColumn } from '@/lib/checklists/templateList';
import type { ChecklistTemplate } from '@/types/settings';

const CHECKLIST_COLUMNS: DataGridColumn<ChecklistColumn>[] = [
  { key: 'definition', label: 'Definition', sortable: true, width: 360 },
  { key: 'status', label: 'Status', sortable: true, align: 'center', width: 120 },
  { key: 'visibility', label: 'Visibility', sortable: true, align: 'center', width: 160 },
  { key: 'sections', label: 'Sections', sortable: true, align: 'center', width: 120 },
  { key: 'items', label: 'Items', sortable: true, align: 'center', width: 120 },
  { key: 'jobTypes', label: 'Job Types', sortable: true, width: 260 },
  { key: 'version', label: 'Version', sortable: true, align: 'center', width: 110 },
  { key: 'updatedAt', label: 'Updated', sortable: true, hide: true, width: 160 },
];

interface ChecklistTemplateListProps {
  templates: ChecklistTemplate[];
  sourceCount: number;
  totalCount: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  showClearAll: boolean;
  activeFilterCount: number;
  filterContent: ReactNode;
  onResetFilters: () => void;
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  onView: (template: ChecklistTemplate) => void;
  onEdit: (template: ChecklistTemplate) => void;
  onDuplicate: (template: ChecklistTemplate) => void;
  onArchive: (template: ChecklistTemplate) => void;
  onRestore: (template: ChecklistTemplate) => void;
  archivePending?: boolean;
  restorePending?: boolean;
  sortKey: ChecklistColumn;
  sortDir: DataGridSortDir;
  onSortChange: (key: ChecklistColumn | null, dir: DataGridSortDir | null) => void;
  jobTypeNames: (template: ChecklistTemplate) => string;
  totalItems: (template: ChecklistTemplate) => number;
}

export function ChecklistTemplateList({
  templates,
  sourceCount,
  totalCount,
  isLoading,
  search,
  onSearchChange,
  showClearAll,
  activeFilterCount,
  filterContent,
  onResetFilters,
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  onView,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  archivePending,
  restorePending,
  sortKey,
  sortDir,
  onSortChange,
  jobTypeNames,
  totalItems,
}: ChecklistTemplateListProps) {
  function renderCell(key: ChecklistColumn, template: ChecklistTemplate) {
    const isArchived = template.status === 'ARCHIVED' || template.isDeleted;
    const linkedJobTypeNames = jobTypeNames(template);

    switch (key) {
      case 'definition':
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-medium">{template.name}</p>
                {isArchived ? (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    Archived
                  </Badge>
                ) : null}
              </div>
              {template.description ? (
                <p className="truncate text-xs text-muted-foreground">{template.description}</p>
              ) : null}
            </div>
          </div>
        );
      case 'status':
        return (
          <StatusChip
            status={isArchived ? 'ARCHIVED' : 'ACTIVE'}
            label={isArchived ? 'Archived' : 'Active'}
            variant="table"
          />
        );
      case 'visibility':
        return <ChecklistVisibilityBadge template={template} />;
      case 'sections':
        return <span className="tabular-nums">{template.sections.length}</span>;
      case 'items':
        return <span className="tabular-nums">{totalItems(template)}</span>;
      case 'jobTypes':
        return linkedJobTypeNames ? (
          <p className="truncate text-sm text-muted-foreground">{linkedJobTypeNames}</p>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'version':
        return (
          <Badge variant="outline" className="font-mono text-xs">
            v{template.version ?? 1}
          </Badge>
        );
      case 'updatedAt':
        return <span className="text-muted-foreground">{formatShortDate(template.updatedAt)}</span>;
    }
  }

  return (
    <ListSurface>
      <DataGrid
        columns={CHECKLIST_COLUMNS}
        rows={templates}
        getRowKey={(template) => template.id}
        renderCell={(key, template) => renderCell(key, template)}
        storageKey="settings-checklists"
        emptyTitle={sourceCount === 0 ? 'No checklists yet' : 'No matching checklists found'}
        searchValue={search}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search checklists..."
        showClearAll={showClearAll}
        onClearAll={onResetFilters}
        activeFilterCount={activeFilterCount}
        filterSheet={{
          title: 'Filters',
          onReset: onResetFilters,
          resetDisabled: activeFilterCount === 0 && !search,
          content: filterContent,
        }}
        isLoading={isLoading}
        onRowClick={(template) => onView(template)}
        renderRowActions={(template) => (
          <ChecklistRowActions
            template={template}
            archivePending={archivePending}
            restorePending={restorePending}
            onView={() => onView(template)}
            onEdit={() => onEdit(template)}
            onDuplicate={() => onDuplicate(template)}
            onArchive={() => onArchive(template)}
            onRestore={() => onRestore(template)}
          />
        )}
        actionsColumnWidth={48}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortChange={onSortChange}
        sortingOrder={['asc', 'desc']}
        rowHeight={56}
        persistenceVersion="settings-v3"
        pagination={{
          count: totalCount,
          singular: 'checklist',
          page: currentPage,
          totalPages,
          onPrevious,
          onNext,
        }}
      />
    </ListSurface>
  );
}

function ChecklistRowActions({
  template,
  archivePending = false,
  restorePending = false,
  onView,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
}: {
  template: ChecklistTemplate;
  archivePending?: boolean;
  restorePending?: boolean;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const isArchived = template.status === 'ARCHIVED' || template.isDeleted;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onView}>
          <Eye className="h-3.5 w-3.5" />
          View
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDuplicate}>
          <Copy className="h-3.5 w-3.5" />
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isArchived ? (
          <DropdownMenuItem disabled={restorePending} onSelect={onRestore}>
            <ArchiveRestore className="h-3.5 w-3.5" />
            Restore
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem variant="destructive" disabled={archivePending} onSelect={onArchive}>
            <Archive className="h-3.5 w-3.5" />
            Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatShortDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}
